import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { HamburgerMenuIcon, Cross2Icon } from '@radix-ui/react-icons';
import { useTranslation } from "react-i18next";

// @ts-ignore
import { Eclipse } from "react-loading-io";

import { setLogout } from "@app/store/user.store";
import { useCartQuantity, useCartSyncronized, useCartLoadFailed, useCartExpiry } from '@app/store/selectors';
import { useAppSettings } from '@app/store/settings.store';

import useIsUserLoggedIn from "@app/utils/user";
import { useCurrentLanguage, useSwitchLanguage } from "@app/utils/language";
import UserService from "@app/services/user.service";

import { img } from "@app/style/utils";
import { showGlobalModal } from "@app/utils/modals";
import PromoBar from "@app/components/ui/promo-bar";
import styles from "./header.module.css";

type HeaderStyles = Record<string, string>;
import { FoodyTooltip } from "@app/design-system/components/FoodyTooltip";
/*
import flagBG from "@app/scss/img/foodyto/bg.svg";
import flagEN from "@app/scss/img/foodyto/en.svg";
*/
function LanguageSelector({ styles }: { styles: HeaderStyles }) {
    const currentLanguage = useCurrentLanguage();
    const switchLanguage = useSwitchLanguage();

    return (
        <>
            <span className={styles.dropdownMenuOption} onClick={() => switchLanguage(currentLanguage === 'bg' ? 'en' : 'bg')} data-test="header-language-switcher">
                <img src={img('icons/globe.svg')} className="svg-sign-in" alt={''} />{currentLanguage === 'bg' ? 'EN' : 'BG'}
            </span>
        </>
    );
}

function LoggedInLinksMenu() {
    const { t } = useTranslation();

    return (
        <>
            {/*<li>
                <Link to=""
                    className="dropdown-toggle"
                    id="alertsDropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClick={(e) => {
                        e.preventDefault();
                        setShowNotifications(!showNotifications);
                    }}>
                    <img src={img('icons/alarm.svg')} className="svg-alarm me-2 me-lg-0" alt={''} />
                    <span className="d-lg-none">{t('Известия')}</span>
                </Link>
                <div className={`dropdown-menu overflow-hidden dropdown-notifications${showNotifications ? ' show' : ''}`} aria-labelledby="alertsDropdown">
                    <h6 className="dropdown-header text-center">{t('Нови')}:</h6>
                    <NotificationsComponent/>
                    <Link className="dropdown-item small text-center" to="#">{t('VIEW_ALL')}</Link>
                </div>
            </li>*/}
            <li><Link to="/home">{t('Аз Foody-то')}</Link></li>
            <li><Link to="/offers">{t('Хапни')}</Link></li>
            <li><Link to="/vendors">{t('Места')}</Link></li>
            <li><Link to="/blog">{t('Блог')}</Link></li>
            <div className="top-menu-auth-options">
                <li><Link to="/home"><span>{t('Дашборд')}</span></Link></li>
                <li><Link to="/past-orders"><span>{t('Поръчки')}</span></Link></li>
                <li><Link to="/profile"><span>{t('Профил')}</span></Link></li>
                <li><Link to="/notifications"><span>{t('Известия')}</span></Link></li>
            </div>
        </>
    );
}

function LoggedOutLinksMenu() {
    const { t } = useTranslation();

    return (
        <>
            <li><Link to="/offers">{t('Хапни')}</Link></li>
            <li><Link to="/partners">{t('Стани партньор')}</Link></li>
            <li><Link to="/blog">{t('Блог')}</Link></li>
            <li><Link to="/about">{t('За нас')}</Link></li>
        </>
    );
}

function LinksMenu() {
    const isLoggedIn = useIsUserLoggedIn();

    return (
        <ul>
            {isLoggedIn
                ? <LoggedInLinksMenu />
                : <LoggedOutLinksMenu />}
        </ul>
    );
}

function LoggedInIconsMenu({ styles }: { styles: HeaderStyles }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loginEnabled } = useAppSettings();

    const logout = (e: any) => {
        e?.preventDefault();

        UserService
            .logout()
            .then(res => {
                dispatch(setLogout());
                localStorage.removeItem('jwt_token');

                window.location.pathname = '/';
            })
            .catch(e => console.error(e));
    };

    return (
        <>
            <li>
                <span className={styles.dropdownMenuOption} onClick={logout} data-test="header-logout">
                    <img src={img('icons/sign-out.svg')} className="svg-sign-out me-2" alt={''} />
                    {t('Изход')}
                </span>
            </li>
            <li className={`${styles.lang} me-2 ${loginEnabled ? 'lang-hide-mobile' : ''}`}>
                <LanguageSelector styles={styles} />
            </li>
        </>
    );
}

function LoggedOutIconsMenu({ styles }: { styles: HeaderStyles }) {
    const { t } = useTranslation();
    const { loginEnabled } = useAppSettings();

    return (
        <>
            {loginEnabled ?
                <li>
                    <span className={styles.dropdownMenuOption} onClick={() => showGlobalModal('login')} data-test="header-sign-in">
                        <img src={img('icons/sign-in.svg')} className="svg-sign-in" alt={''} />{t('SIGN_IN')}
                    </span>
                </li> : null}
            <li className={`${styles.lang} ${loginEnabled ? 'lang-hide-mobile' : ''}`}>
                <LanguageSelector styles={styles} />
            </li>
        </>
    );
}

function IconsMenu({ styles }: { styles: HeaderStyles }) {
    const { t } = useTranslation();
    const syncronized = useCartSyncronized();
    const cartCount = useCartQuantity();
    const cartLoadFailed = useCartLoadFailed();
    const cartExpiry = useCartExpiry();
    const isLoggedIn = useIsUserLoggedIn();
    const { loginEnabled } = useAppSettings();

    return (
        <ul id="right-menu" className={`${styles.rightMenu} ${loginEnabled ? 'ms-lg-2' : ''}`}>
            {isLoggedIn
                ? <LoggedInIconsMenu styles={styles} />
                : <LoggedOutIconsMenu styles={styles} />}
            <li className={styles.cart}>
                {syncronized &&
                    <Link
                        to="/checkout"
                        title={t('Количка')}
                        data-test={cartLoadFailed ? 'cart-load-error' : undefined}>
                        <>
                            <img className={`${styles.cartIcon}${cartLoadFailed ? ` ${styles.cartIconError}` : ''}`} src={img('icons/cart-alt.svg')} alt={''} />
                            {cartCount && !cartLoadFailed ?
                                <span className={styles.count} data-test="cart-count">
                                    {cartCount}
                                </span> : null}
                            {cartExpiry && (
                                <FoodyTooltip
                                    side="bottom"
                                    content={
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {cartExpiry.vendors.map((v, i) => (
                                                <span key={i}>{v.name} {t('CLOSES_IN')} {v.minutes} min</span>
                                            ))}
                                        </div>
                                    }
                                >
                                    <span
                                        className={styles.cartExpiryBadge}
                                        data-test="cart-expiry-badge"
                                        data-test-minutes={cartExpiry.minMinutes}
                                    >
                                        {cartExpiry.minMinutes}m
                                    </span>
                                </FoodyTooltip>
                            )}
                        </>
                    </Link>}
                {!syncronized &&
                    <Eclipse size={22} width={6} color="#333" />}
            </li>
        </ul>
    );
}

export default function Header({ sticky }: { sticky: boolean }) {
    const location = useLocation();
    const { loginEnabled, promoEnabled } = useAppSettings();

    const [opened, setOpened] = useState(false);

    useEffect(() => {
        setOpened(false);
    }, [location]);

    return (
        <header
            className={`${styles.root} ${sticky ? `${styles.header} header clearfix element_to_stick` : `${styles.headerIn} ${styles.dark} clearfix`}${promoEnabled ? '' : ` ${styles.noPromoBar}`}`}
            data-test="header">
            <PromoBar headerMenuOpened={opened} headerStyles={styles} />
            <div className="container-wide">
                <div id="logo" className={styles.logo}>
                    <Link to="/">
                        <img src={img('logo/foody-underlined.png')} width="110" alt="" className={styles.standard} />
                        <img src={img('logo/foody-dark-underlined.png')} width="130" alt="" className={styles.white} />
                    </Link>
                </div>

                <IconsMenu styles={styles} />

                <a onClick={() => setOpened(!opened)} className={styles.openClose} aria-label="Toggle menu" data-test="header-hamburger">
                    <HamburgerMenuIcon /><span>Menu</span>
                </a>

                {loginEnabled
                    ? <div className={styles.leftMenu}><ul><li><LanguageSelector styles={styles} /></li></ul></div>
                    : null}

                <nav className={`${styles.mainMenu} ${opened ? styles.show : ''}`} data-test="header-nav">
                    <div id="header_menu" className={styles.headerMenu}>
                        <a onClick={() => setOpened(!opened)} className={styles.openClose} aria-label="Close menu" data-test="header-menu-close">
                            <Cross2Icon /><span>Menu</span>
                        </a>
                        <Link to="/"><img src={img('logo/foody-dark-underlined.png')} width="140" alt="" /></Link>
                    </div>
                    <LinksMenu />
                </nav>
            </div>
        </header>
    );
}
